import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { ConversationDTO, ConversationMessage, SuggestedAnswerDTO } from 'services/api/models/ConversationDTO';
import { ConversationMetadata, ConversationType } from 'store/conversations/types';
import { selectCurrentUser } from 'store/users/selectors';

/**
 * The conversations list is the single source of truth for everything we display about
 * conversation rows (metadata, ordering, owner, status, last_message, unread counts…).
 * It lives in TanStack Query under the `conversationsQueryKey` so any component or
 * websocket handler can read or update it through the same cache primitives without a
 * separate Redux mirror.
 */
export const conversationsQueryKey = ['conversations'] as const;

const noConversations: ConversationDTO[] = [];

export const conversationsQueryOptions = (isCaregiver: boolean) =>
  queryOptions({
    queryKey: conversationsQueryKey,
    queryFn: () =>
      isCaregiver
        ? ApiDoctorConversationsManager.getDoctorConversationsList()
        : ApiPatientConversationsManager.getPatientConversationsList(),
    // We refresh explicitly: on websocket reconnect (gap recovery) and through
    // the `setQueryData` calls driven by the websocket handler on every event.
    staleTime: Infinity,
  });

/**
 * Compute the segmented "bucket" a conversation belongs to from its status. Patient
 * conversations don't have a status; they always render as `assigned`.
 */
export function getConversationType(conversation: ConversationDTO, currentUserId: string): ConversationType {
  const { status, current_owner: owner } = conversation;
  if (!status) return 'assigned';

  if (status.value === 'ASSIGNED_TO_CAREGIVER') return 'assigned';
  // FIRST_ASSIGNMENT_REJECTED / FIRST_ASSIGNMENT_TIMED_OUT (GYN-264) are back
  // to first-come-first-served — treated exactly like WAITING_FOR_FIRST_RESPONSE.
  if (
    status.value === 'WAITING_FOR_FIRST_RESPONSE' ||
    status.value === 'FIRST_ASSIGNMENT_REJECTED' ||
    status.value === 'FIRST_ASSIGNMENT_TIMED_OUT'
  )
    return 'unassigned';
  if (status.value === 'ASSIGNMENT_TO_GROUP_ASKED') {
    if (owner?.id === currentUserId) return 'waitingForReassignment';
    return 'unassigned';
  }
  if (status.value === 'ASSIGNMENT_TO_CAREGIVER_ASKED') {
    if (owner?.id === currentUserId) return 'waitingForReassignment';
    return 'myRequest';
  }

  if (status.value === 'CLOSED') return 'closed';
  if (status.value === 'ON_HOLD') return 'onHold';

  // TODO this ain't great; raise instead?
  return 'assigned';
}

/**
 * Read the conversations list straight out of the TanStack cache. Returns an empty array
 * when the query hasn't run yet or when there's no current user (e.g. the very first
 * paint after login).
 */
export function useConversationsList(): ConversationDTO[] {
  const currentUser = useSelector(selectCurrentUser);
  const { data } = useQuery({
    ...conversationsQueryOptions(!!currentUser?.is_caregiver),
    enabled: !!currentUser,
  });
  return data ?? noConversations;
}

/** True the first time the query has resolved with any data (success or empty). */
export function useLoadedConversationsOnce(): boolean {
  const currentUser = useSelector(selectCurrentUser);
  const { isSuccess } = useQuery({
    ...conversationsQueryOptions(!!currentUser?.is_caregiver),
    enabled: !!currentUser,
  });
  return isSuccess;
}

/** Translate the TanStack query state into the loading-state strings the UI consumes. */
export function useConversationsLoadingStatus(): 'LOADING' | 'REFRESHING' | 'SUCCESS' | 'ERROR' {
  const currentUser = useSelector(selectCurrentUser);
  const { isPending, isFetching, isSuccess, isError } = useQuery({
    ...conversationsQueryOptions(!!currentUser?.is_caregiver),
    enabled: !!currentUser,
  });
  if (isError) return 'ERROR';
  if (isPending) return 'LOADING';
  if (isFetching) return 'REFRESHING';
  if (isSuccess) return 'SUCCESS';
  return 'LOADING';
}

/**
 * Datetime of the latest activity in a conversation: its last message, or — for
 * conversations without any message yet (initiations, conversations opened after a
 * medical questionnaire was completed) — its latest status change. `undefined` when
 * neither is known.
 */
export function getLatestActivityDate(conversation: ConversationDTO): Date | undefined {
  const datetime = conversation.last_message?.sent_at ?? conversation.latest_status_change_datetime;
  return datetime ? new Date(datetime) : undefined;
}

/**
 * Compare two conversations by their latest activity, newest first.
 * Conversations without any known activity date are sorted to the end.
 */
export function compareByLatestMessageDesc(a: ConversationDTO, b: ConversationDTO): number {
  const aTime = getLatestActivityDate(a)?.getTime() ?? 0;
  const bTime = getLatestActivityDate(b)?.getTime() ?? 0;
  return bTime - aTime;
}

/**
 * Compare two conversations by their latest activity, oldest first.
 * Conversations without any known activity date are sorted to the end.
 */
export function compareByLatestMessageAsc(a: ConversationDTO, b: ConversationDTO): number {
  const aTime = getLatestActivityDate(a)?.getTime() ?? Infinity;
  const bTime = getLatestActivityDate(b)?.getTime() ?? Infinity;
  return aTime - bTime;
}

/**
 * For caregivers, returns conversations whose computed bucket equals `type`. For
 * patients (single inbox, no buckets), returns all conversations regardless of `type`,
 * matching the legacy selector behaviour.
 *
 * Conversations are sorted by latest message datetime (most recent first) for most
 * types. `myRequest` and `waitingForReassignment` are sorted oldest-first so the most
 * urgent (longest-waiting) conversations surface at the top.
 * Sorting for the 'assigned' type in the "My Conversations" caregiver section
 * is handled separately in MessagingHome via `useMyConversationsSplit`.
 */
export function useConversationsByType(type: ConversationType): ConversationDTO[] {
  const conversations = useConversationsList();
  const currentUser = useSelector(selectCurrentUser);
  const currentUserId = currentUser?.id ?? '';
  return useMemo(() => {
    if (!currentUser?.is_caregiver) {
      return [...conversations].sort(compareByLatestMessageDesc);
    }
    const filtered = conversations.filter(c => getConversationType(c, currentUserId) === type);
    const comparator =
      type === 'myRequest' || type === 'waitingForReassignment'
        ? compareByLatestMessageAsc
        : compareByLatestMessageDesc;
    return [...filtered].sort(comparator);
  }, [conversations, currentUserId, currentUser?.is_caregiver, type]);
}

/**
 * For the caregiver "My Conversations" section (assigned conversations), splits into:
 * - `needsReply`: conversations where the last message was sent by the patient,
 *   sorted oldest first (most urgent at the top).
 * - `replied`: conversations where the last message was sent by the caregiver,
 *   sorted newest first.
 */
export function useMyConversationsSplit(assignedConversations: ConversationDTO[]): {
  needsReply: ConversationDTO[];
  replied: ConversationDTO[];
} {
  const currentUser = useSelector(selectCurrentUser);
  return useMemo(() => {
    const needsReply: ConversationDTO[] = [];
    const replied: ConversationDTO[] = [];
    for (const conv of assignedConversations) {
      const lastSenderId = conv.last_message?.sender?.id;
      const sentByCurrentUser = lastSenderId === currentUser?.id;
      const awaitingReviewStart = conv.questionnaire_needs_review && !conv.questionnaire_review_started;
      if (sentByCurrentUser && !awaitingReviewStart) {
        replied.push(conv);
      } else {
        needsReply.push(conv);
      }
    }
    needsReply.sort(compareByLatestMessageAsc);
    replied.sort(compareByLatestMessageDesc);
    return { needsReply, replied };
  }, [assignedConversations, currentUser?.id]);
}

/**
 * Returns a `{ conversation, type }` pair for the conversation with the given id; this
 * is the shape components used to read out of the legacy Redux slice. `undefined` when
 * the conversation isn't in the cache yet.
 */
export function useConversationMetadata(conversationId: string | undefined): ConversationMetadata | undefined {
  const conversations = useConversationsList();
  const currentUser = useSelector(selectCurrentUser);
  return useMemo(() => {
    if (!conversationId) return undefined;
    const found = conversations.find(c => c.id === conversationId);
    if (!found) return undefined;
    return {
      conversation: found,
      type: getConversationType(found, currentUser?.id ?? ''),
    };
  }, [conversations, conversationId, currentUser?.id]);
}

/**
 * Helper hook returning a stable `setConversations(updater)` callback that every
 * mutator (websocket handlers, REST mutations, optimistic updates from components)
 * goes through to update the cache. `updater` receives the previous list (or [] when
 * empty) and must return the new list. Single chokepoint = single place to reason
 * about ordering and identity.
 */
export function useUpdateConversations() {
  const queryClient = useQueryClient();
  return (updater: (previous: ConversationDTO[]) => ConversationDTO[]) => {
    queryClient.setQueryData<ConversationDTO[]>(conversationsQueryKey, previous => updater(previous ?? []));
  };
}

/**
 * Stable query-key factory for a single conversation's messages list.
 * Shell.tsx uses this key directly (without the hook) to update the cache
 * from WebSocket events.
 */
export const conversationMessagesQueryKey = (conversationId: string) =>
  ['conversation', conversationId, 'messages'] as const;

/**
 * Fetches the messages for the currently open conversation and stores them in
 * TanStack Query under `conversationMessagesQueryKey(conversationId)`.
 *
 * Using TanStack (instead of the previous useRequest/Redux flow) gives us a
 * reliable `isLoading` / `isSuccess` state that ConversationPage uses to
 * disable the send button until the list is ready. This prevents the race
 * where a sent message's WebSocket echo arrives before the cache is populated
 * and is silently dropped by setQueryData's `if (!prev) return prev` guard.
 */
export function useConversationMessages(conversationId: string | undefined) {
  const currentUser = useSelector(selectCurrentUser);
  return useQuery({
    queryKey: conversationMessagesQueryKey(conversationId ?? ''),
    queryFn: async (): Promise<ConversationMessage[]> => {
      const result = currentUser?.is_caregiver
        ? await ApiDoctorConversationsManager.getDoctorConversationMessageList({
            conversationId: conversationId!,
          })
        : await ApiPatientConversationsManager.getPatientConversationsMessageList({
            conversationId: conversationId!,
          });
      return result.messages ?? [];
    },
    enabled: !!conversationId && !!currentUser,
    staleTime: Infinity,
  });
}

/**
 * Stable query-key factory for a single conversation's live reply suggestion
 * (GYN-266). Shell.tsx uses this key directly (without the hook) to update
 * the cache from the `reply_suggestion.created` WebSocket event.
 */
export const suggestedAnswerQueryKey = (conversationId: string) =>
  ['conversation', conversationId, 'suggestedAnswer'] as const;

/**
 * Fetches (and caches) the caregiver-facing reply suggestion for the
 * currently open conversation, if any. Covers the case where a suggestion
 * was generated before the caregiver opened the conversation; once open,
 * new suggestions arrive live via the `reply_suggestion.created` WebSocket
 * event (see Shell.tsx), which writes straight into this same cache entry.
 */
export function useSuggestedAnswer(conversationId: string | undefined) {
  const currentUser = useSelector(selectCurrentUser);
  return useQuery({
    queryKey: suggestedAnswerQueryKey(conversationId ?? ''),
    queryFn: (): Promise<SuggestedAnswerDTO | null> =>
      ApiDoctorConversationsManager.getSuggestedAnswer(conversationId!),
    enabled: !!conversationId && !!currentUser?.is_caregiver,
    staleTime: Infinity,
  });
}
